import router from '@/router'
import { UserService } from '@/services/user.service'
import TenantService from '@/services/tenant.service'
import TokenService from '@/services/storage.service'
import {
  clearAuthSession,
  forceLogoutToLogin,
  isAuthSessionExpired,
  resetAuthSessionExpired,
  SessionExpiredError
} from '@/auth/session'
import { clearTemporaryPasswordChangeSession, getTemporaryPasswordChangeUser, isNewPasswordSameAsTemporary } from '@/auth/temporaryPassword'
import { ensureValidAccessToken } from '@/auth/refresh'

function parseModuleConfig(config) {
  if (!config) return {}
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof config === 'object' && !Array.isArray(config)) {
    return config
  }
  return {}
}

export const authActions = {
  async loginUser({ commit, dispatch }, userData) {
    try {
      const response = await UserService.login(userData.email, userData.password, userData.token, userData.productId)

      if (response?.passwordChangeRequired) {
        await router.push({ name: 'change-password' })
        return
      }

      if (response?.token) {
        resetAuthSessionExpired()
        await dispatch('loadTenantModules')
        if (!userData.productId) {
          const redirect = userData.redirect
          if (redirect && typeof redirect === 'string' && redirect.startsWith('/')) {
            await router.push(redirect)
          } else {
            await router.push({ name: 'dashboard' })
          }
        } else {
          await router.push({
            name: 'product',
            params: { id: userData.productId }
          })
        }
      }
    } catch (error) {
      if (error?.errors !== undefined) {
        commit('SET_REGISTER_ERRORS', error)
      }
    }
  },

  async completeForcedPasswordChange({ dispatch }, credentials) {
    const storedUser = getTemporaryPasswordChangeUser()
    const userEmail = storedUser?.email || null
    const newPassword = credentials.password

    if (isNewPasswordSameAsTemporary(newPassword)) {
      return { success: false, sameAsTemporaryPassword: true }
    }

    const response = await UserService.changeForcedPassword(credentials)

    if (response?.status !== 200) {
      return { success: false, response }
    }

    const responseEmail = response.data?.data?.user?.email
    const email = userEmail || responseEmail

    clearTemporaryPasswordChangeSession()

    if (!email) {
      return { success: false, response }
    }

    try {
      const loginResult = await UserService.login(email, newPassword)

      if (loginResult?.passwordChangeRequired || !loginResult?.token) {
        return { success: false, passwordChanged: true, response }
      }

      resetAuthSessionExpired()
      await dispatch('loadTenantModules')
      await dispatch('me', { requireFresh: true })
      await router.replace({ name: 'dashboard' })

      return { success: true }
    } catch (error) {
      return { success: false, passwordChanged: true, response: error?.response || error }
    }
  },

  async me({ commit }, options = {}) {
    const requireFresh = options?.requireFresh === true

    if (!TokenService.getToken() || isAuthSessionExpired()) {
      clearAuthSession()
      throw new SessionExpiredError()
    }

    try {
      await ensureValidAccessToken()

      const adminData = !requireFresh ? TokenService.getAdminData() : null

      if (!adminData) {
        const response = await UserService.me()
        if (!response?.data?.data || response?.status === 401) {
          clearAuthSession()
          throw new SessionExpiredError()
        }

        commit('SET_USER', response.data.data)
        commit('SET_USER_ADMIN', response.data.data)
        commit('SET_ADMIN_ROLE', response.data.data.roles)
        commit('SET_IS_MAINTANANCE', response.data.data.is_maintenance)

        TokenService.saveAdminData(JSON.stringify(response.data.data))
        resetAuthSessionExpired()
      } else {
        const data = JSON.parse(adminData)
        commit('SET_USER_ADMIN', data)
        commit('SET_ADMIN_ROLE', data.roles)
        commit('SET_IS_MAINTANANCE', data.is_maintenance)
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        throw err
      }

      clearAuthSession()
      throw new SessionExpiredError()
    }
  },

  async adminMe({ commit }) {
    try {
      const response = await UserService.me()
      if (!response?.data?.data || response?.status === 401) {
        forceLogoutToLogin()
        return
      }
      commit('SET_USER_ADMIN', response.data.data)
    } catch (err) {
      forceLogoutToLogin()
    }
  },

  async loadTenantModules({ commit }, countryCode = null) {
    try {
      const response = await TenantService.getTenantModules(countryCode)

      if (response && response.status === 200 && response.data) {
        console.log('📦 Raw API Response:', response.data)

        const apiData = response.data.data || response.data

        const tenantId = apiData.tenant_id || TokenService.getTenant()
        const modulesData = apiData.modules || []
        const userPermissions = apiData.userPermissions || []

        console.log('📋 Processing modules:', {
          tenantId,
          modulesCount: modulesData.length,
          userPermissionsCount: userPermissions.length,
          modules: modulesData.map((m) => ({ id: m.id, slug: m.slug }))
        })

        const modulesArray = modulesData.map((module) => {
          const hasActiveFlag = module.active !== undefined && module.active !== null
          const isActive = hasActiveFlag ? module.active === true || module.active === 1 || module.active === '1' : true

          return {
            id: module.id,
            slug: module.slug,
            name: module.name,
            client_type: module.client_type,
            config: parseModuleConfig(module.config),
            active: isActive,
            enabled: isActive
          }
        })

        const moduleSlugs = modulesArray.filter((module) => module.enabled).map((module) => module.slug)

        const modulesBySlug = {}
        modulesArray.forEach((module) => {
          modulesBySlug[module.slug] = module
        })

        commit('SET_TENANT_MODULES', modulesArray)
        commit('SET_AVAILABLE_MODULES', moduleSlugs)
        commit('SET_USER_PERMISSIONS', userPermissions)
        commit('SET_TENANCY_ENABLED', true)
        commit('SET_TENANT_ID', tenantId)

        commit('SET_MODULES_BY_SLUG', modulesBySlug)

        const mergedConfig = {}
        modulesArray.forEach((module) => {
          if (module.config && typeof module.config === 'object') {
            Object.assign(mergedConfig, module.config)
          }
        })
        commit('SET_TENANT_CONFIG', mergedConfig)

        console.log('✅ Loaded Tenant Config from API:', {
          tenantId,
          modulesCount: modulesData.length,
          moduleSlugs,
          modulesBySlugKeys: Object.keys(modulesBySlug),
          availableModules: moduleSlugs
        })
      } else {
        console.warn('⚠️ Invalid API response:', response)
      }
    } catch (error) {
      console.error('❌ Failed to load tenant modules:', error)
      commit('SET_TENANCY_ENABLED', false)
    }
  }
}
